<template>
    <not-found v-if="storefrontNode === null" />
    <template v-else-if="storefrontNode.__typename === 'StorefrontPlan'">
        <purchase-membership-wrapper
            v-if="storefrontNode.planType === 'MEMBERSHIP'"
            :plan-id="storefrontNode.planId"
        />
        <purchase-program-wrapper
            v-else-if="storefrontNode.planType === 'PROGRAM'"
            :plan-id="storefrontNode.planId"
        />
        <purchase-agreement-wrapper
            v-else-if="storefrontNode.planType === 'AGREEMENT'"
            :plan-id="storefrontNode.planId"
        />
        <purchase-pass-wrapper
            v-else-if="storefrontNode.planType === 'PASS'"
            :plan-id="storefrontNode.planId"
        />
        <purchase-gift-card-wrapper
            v-else-if="storefrontNode.planType === 'GIFTCARD'"
            :plan-id="storefrontNode.planId"
        />
        <not-found v-else />
    </template>

    <storefront-catalog
        v-else-if="storefrontNode.__typename === 'StorefrontCatalog'"
        :catalog="storefrontNode"
        :storefront-prefix="storefrontPrefix"
    />
    <storefront-calendar
        v-else-if="storefrontNode.__typename === 'StorefrontCalendar'"
        :calendar="storefrontNode"
        :storefront-prefix="props.storefrontPrefix"
    />
    <not-found v-else />
</template>

<script setup lang="ts">
import { useRoute } from '#app'
import { computed, inject } from 'vue'
import type { Ref } from 'vue'
import type {
    StorefrontCatalogScalar,
    StorefrontNode
} from '~/resources/scalars/storefront-catalog-scalar'
import NotFound from '~/components/NotFound/NotFound.vue'
import PurchaseProgramWrapper from '~/components/PurchaseContract/Program/PurchaseProgramWrapper.vue'
import PurchaseAgreementWrapper from '~/components/PurchaseContract/Agreement/PurchaseAgreementWrapper.vue'
import StorefrontCatalog from '~/components/Storefront/StorefrontCatalog.vue'
import StorefrontCalendar from '~/components/Storefront/StorefrontCalendar.vue'
import PurchaseMembershipWrapper from '~/components/PurchaseContract/Membership/PurchaseMembershipWrapper.vue'
import PurchasePassWrapper from '~/components/PurchaseContract/Pass/PurchasePassWrapper.vue'
import PurchaseGiftCardWrapper from '~/components/PurchaseContract/GiftCard/PurchaseGiftCardWrapper.vue'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useHead } from 'unhead'
import { unsafe_facilityCreateContractLink } from '~/composables/routing'
import { useCanonicalPath } from '~/composables/use-canonical-link'
import { useStorefrontCatalogCalendarSeo } from '~/composables/use-storefront-catalog-calendar-seo'

const props = defineProps<{ storefrontPrefix: string }>()

const route = useRoute()
const selectedSlug = computed(() => {
    const slugParam = route.params.slug
    if (typeof slugParam === 'string') {
        return slugParam
    } else {
        return undefined
    }
})

const catalog = inject<Ref<StorefrontCatalogScalar | null>>('CATALOG')

function getNode(node: StorefrontNode): StorefrontNode | null {
    if (
        node.__typename !== 'StorefrontCalendarGroup' &&
        node.slug === selectedSlug.value
    ) {
        return node
    } else if (node.__typename === 'StorefrontPlan') {
        return null
    } else {
        for (const child of node.children) {
            const found = getNode(child)
            if (found !== null) {
                return found
            }
        }

        return null
    }
}

const storefrontNode = computed<StorefrontNode | null>(() => {
    if (catalog === undefined) {
        return null
    }

    if (catalog.value === null) {
        return null
    }

    const children = catalog.value.children
    if (children === undefined) {
        return null
    }

    for (const child of children) {
        const found = getNode(child)
        if (found !== null) {
            return found
        }
    }

    return null
})

const canonicalPath = computed(() => {
    if (storefrontNode.value?.__typename !== 'StorefrontPlan') {
        return null
    }

    // Storefront node purchase pages duplicate the direct purchase pages, so
    // search engines should consolidate on the direct purchase URL.
    return unsafe_facilityCreateContractLink(
        props.storefrontPrefix,
        storefrontNode.value.planType,
        storefrontNode.value.slug
    )
})
useCanonicalPath(canonicalPath)

const storefrontSeoNode = computed(() => {
    const node = storefrontNode.value
    if (
        node?.__typename === 'StorefrontCatalog' ||
        node?.__typename === 'StorefrontCalendar'
    ) {
        return node
    }

    // Purchase pages own their metadata through the plan presentation SEO path.
    return null
})
useStorefrontCatalogCalendarSeo(storefrontSeoNode)

const activeFacility = useActiveFacility()
useHead({
    title: () =>
        activeFacility.value.title +
        (storefrontNode.value !== null
            ? ' | ' + storefrontNode.value.title
            : '')
})
</script>
